
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useEtapas } from './useEtapasData';
import { useTags } from './useTagsData';
import { useSupabaseChat } from './useSupabaseChat';

/**
 * 🎯 Hook Principal para Gerenciamento de Dados do Supabase
 * 
 * 📋 RESPONSABILIDADES:
 * - Agregar dados de diferentes domínios (leads, etapas, tags, chat)
 * - Gerenciar estado de loading consolidado
 * - Configurar subscriptions Realtime para atualizações automáticas
 * - Fornecer interface unificada para componentes
 * - Filtrar leads para mostrar apenas aqueles com mensagens de chat
 * 
 * 🔗 HOOKS ESPECIALIZADOS UTILIZADOS:
 * - useEtapas: Etapas do kanban e configurações
 * - useTags: Tags/categorias para organização
 * - useSupabaseChat: Mensagens e chat em tempo real
 * 
 * 🔄 REALTIME:
 * - Subscription para novos mensagens no chat
 * - Atualização automática de contadores
 * - Sincronização em tempo real entre usuários
 * 
 * ⚡ PERFORMANCE:
 * - Hooks especializados com cache independente
 * - Loading states otimizados
 * - Cleanup adequado de subscriptions
 * 
 * 🎯 CASOS DE USO:
 * - Página principal do chat/kanban
 * - Dashboard com dados consolidados
 * - Relatórios que precisam de múltiplas entidades
 */
export const useSupabaseData = () => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<any[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);

  // 🔗 HOOKS ESPECIALIZADOS
  const { data: etapas = [], isLoading: etapasLoading } = useEtapas();
  const { data: tags = [], isLoading: tagsLoading } = useTags();
  const chatHook = useSupabaseChat();

  // 📊 BUSCAR LEADS COM MENSAGENS
  const fetchLeadsWithMessages = async () => {
    try {
      console.log('🔍 [useSupabaseData] Buscando leads com mensagens...');
      
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('clinica_id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (!userProfile?.clinica_id) {
        console.log('❌ [useSupabaseData] Usuário sem clínica associada');
        setLeads([]);
        return;
      }

      // Busca leads que possuem mensagens em uma única query, evitando
      // URLs longas no .in() para clínicas com muitos leads
      const { data, error } = await supabase
        .rpc('get_leads_with_messages', { p_clinica_id: userProfile.clinica_id });

      if (error) {
        console.error('❌ [useSupabaseData] Erro ao buscar leads com mensagens:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('📭 [useSupabaseData] Nenhum lead com mensagens encontrado');
        setLeads([]);
        return;
      }

      const uniqueLeads = data.map((lead: any) => ({
        ...lead,
        ultima_mensagem_data: lead.data_ultimo_contato,
        total_mensagens: 1,
      }));

      console.log(`✅ [useSupabaseData] Encontrados ${uniqueLeads.length} leads com mensagens`);
      setLeads(uniqueLeads);
    } catch (error) {
      console.error('❌ [useSupabaseData] Erro ao carregar leads:', error);
      setLeads([]);
    } finally {
      setLeadsLoading(false);
    }
  };

  // 📊 LOADING CONSOLIDADO
  useEffect(() => {
    const isStillLoading = leadsLoading || etapasLoading || tagsLoading;
    setLoading(isStillLoading);
  }, [leadsLoading, etapasLoading, tagsLoading]);

  // 🔄 CARREGAR DADOS INICIAIS
  useEffect(() => {
    fetchLeadsWithMessages();
  }, []);

  // 🔄 SUBSCRIPTION REALTIME OTIMIZADA
  // ⚡ PERFORMANCE: Atualização incremental ao invés de recarregar tudo
  useEffect(() => {
    if (!chatHook.isChatDataReady) return;

    const canalMensagens = supabase
      .channel('mensagens-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_mensagens',
        },
        async (payload) => {
          const novaMensagem = payload.new as any;
          console.log('📥 [useSupabaseData] Nova mensagem recebida:', novaMensagem.lead_id);

          // Usa o padrão de atualizador funcional para ler o estado mais recente,
          // evitando o problema de closure desatualizado com leads.find()
          let leadJaExiste = false;

          setLeads(prevLeads => {
            leadJaExiste = prevLeads.some(l => l.id === novaMensagem.lead_id);

            if (!leadJaExiste) return prevLeads; // sem mudança — busca assíncrona abaixo

            // Atualiza timestamp e reordena
            const updatedLeads = prevLeads.map(lead =>
              lead.id === novaMensagem.lead_id
                ? {
                    ...lead,
                    ultima_mensagem_data: novaMensagem.created_at,
                    total_mensagens: (lead.total_mensagens || 0) + 1
                  }
                : lead
            );
            return updatedLeads.sort((a, b) => {
              const dateA = new Date(a.ultima_mensagem_data || a.data_ultimo_contato || a.updated_at);
              const dateB = new Date(b.ultima_mensagem_data || b.data_ultimo_contato || b.updated_at);
              return dateB.getTime() - dateA.getTime();
            });
          });

          // Se o lead não estava na lista, busca e adiciona
          if (!leadJaExiste) {
            console.log('🔍 [useSupabaseData] Novo lead detectado, buscando dados...');
            const { data: leadData } = await supabase
              .from('leads')
              .select('*')
              .eq('id', novaMensagem.lead_id)
              .single();

            if (leadData) {
              setLeads(prev => {
                if (prev.some(l => l.id === leadData.id)) return prev; // já existe
                console.log('✅ [useSupabaseData] Novo lead adicionado à lista');
                return [{ ...leadData, ultima_mensagem_data: novaMensagem.created_at, total_mensagens: 1 }, ...prev];
              });
            }
          }

          // Atualiza contador de mensagens não lidas
          if (novaMensagem.enviado_por === 'lead' && !novaMensagem.lida) {
            chatHook.setMensagensNaoLidas(contadores => ({
              ...contadores,
              [novaMensagem.lead_id]: (contadores[novaMensagem.lead_id] || 0) + 1
            }));
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'leads',
        },
        (payload) => {
          const leadAtualizado = payload.new as any;
          console.log('📝 [useSupabaseData] Lead atualizado (otimizado):', leadAtualizado.id);
          
          // ⚡ OTIMIZAÇÃO: Atualizar apenas o lead específico
          setLeads(prevLeads => 
            prevLeads.map(lead => 
              lead.id === leadAtualizado.id
                ? { ...lead, ...leadAtualizado }
                : lead
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(canalMensagens);
    };
  }, [chatHook.isChatDataReady]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 📤 INTERFACE PÚBLICA
  return {
    // 📊 DADOS DAS ENTIDADES
    leads: Array.isArray(leads) ? leads : [],
    etapas: Array.isArray(etapas) ? etapas : [],
    tags: Array.isArray(tags) ? tags : [],
    
    // 💬 DADOS DO CHAT
    mensagens: chatHook.mensagens || [],
    respostasProntas: chatHook.respostasProntas || [],
    mensagensNaoLidas: chatHook.mensagensNaoLidas || {},
    
    // ⏳ LOADING
    loading,

    // 🔧 FUNÇÕES DO CHAT
    buscarMensagensLead: chatHook.buscarMensagensLead,
    enviarMensagem: chatHook.enviarMensagem,
    marcarMensagensComoLidas: chatHook.marcarMensagensComoLidas,

    // ✅ STATUS DE PRONTIDÃO
    isChatDataReady: chatHook.isChatDataReady,

    // 🔄 FUNÇÃO PARA RECARREGAR DADOS
    refetchLeads: fetchLeadsWithMessages,
  };
};
